import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { VideoPlayer } from '../../components/Molecules';
import { SelectWordModePopup } from '../../components/Popups';
import { CreateGameWordSelectInfoPopup } from '../../components/Popups/CreateGameWordSelectInfoPopup';
import { CreateMemoryGameSelectWordsTemplate } from '../../components/Templates';
import { WordSelectionMode } from '../../constants/MemoryGames/wordSelectionMode';
import { SequenceImageType } from '../../constants/sequenceImages';
import {
  getBibleBoostCreateVideo,
  getMemoryBoostCreateVideo,
} from '../../constants/videos';
import { usePopup } from '../../context';
import { TranslationLocale } from '../../i18n';
import { QuickCreateGameMode } from '../../models/DraftStore';
import { TextOrderingUnit, TextOrderingUnitType } from '../../models/GameStore';
import { WebBoosterType } from '../../models/UserStore';
import { useDraftStore } from '../../stores/DraftStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { getUiLocale } from '../../utils/i18n';
import { selectRandomWords, splitIntoWords } from '../../utils/string';

interface State {
  createType: QuickCreateGameMode | undefined;
  showRequiredWarning: boolean;
  wordIndexes: number[][];
}

const CreateMemoryGameSelectWordsScreen: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();
  const [state, setState] = useState<State>({
    createType: undefined,
    showRequiredWarning: false,
    wordIndexes: [],
  });

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const userAccount = useUserStore((store) =>
    store.getAccount(activeUserId ?? ''),
  );

  const { mode, selectWords, selectWordModes, drafts, locale } = useDraftStore(
    (store) => {
      const _mode = store.getGameMode();
      const _selectWords = store.getSelectWords();
      const _selectWordModes = store.getSelectWordModes();
      const _drafts = store.getGameDrafts();
      const _locale = store.getLocale();
      return {
        mode: _mode,
        selectWords: _selectWords,
        selectWordModes: _selectWordModes,
        drafts: _drafts,
        locale: _locale,
      };
    },
  );

  useEffect(() => {
    const draft = drafts[0];
    if (draft) {
      const answerText = draft.game.answer.text.text;
      const answerWords = splitIntoWords(answerText, locale, {
        withSpace: false,
      });
      const albumName = answerWords
        .reduce(
          (acc, cur) => {
            if (acc.hasExceed || acc.count >= 5) {
              return acc;
            }
            if (cur.isWordLike) {
              const temp = acc.result + cur.text + ' ';
              if (temp.length <= 50) {
                return {
                  result: temp,
                  hasExceed: acc.hasExceed,
                  count: acc.count + 1,
                };
              } else if (acc.count === 0) {
                return {
                  result: temp.slice(0, 50),
                  hasExceed: true,
                  count: acc.count + 1,
                };
              } else {
                return {
                  result: acc.result,
                  hasExceed: true,
                  count: acc.count,
                };
              }
            }
            return acc;
          },
          { result: '', hasExceed: false, count: 0 },
        )
        .result.trim();
      setState((s) => ({ ...s, albumName }));
    }
  }, [drafts, locale]);

  useEffect(() => {
    if (mode) {
      setState((s) => ({ ...s, createType: mode }));
    } else {
      navigate('/create/memory-game/select-type', { replace: true });
    }
  }, [mode, navigate]);

  const selectWordIndex = useCallback((word: TextOrderingUnit[]) => {
    const wordCount = word.reduce(
      (acc, cur) => acc + (cur.type === TextOrderingUnitType.Candidate ? 1 : 0),
      0,
    );
    const indexes = selectRandomWords(
      word.map((unit) => unit.key),
      word.map((unit) =>
        unit.type === TextOrderingUnitType.Candidate ? unit.key.length : 0,
      ),
      Math.min(9, Math.max(1, Math.floor(wordCount * 0.5))),
    );
    return indexes;
  }, []);

  const updateWordIndexes = useCallback(() => {
    const wordIndexes = selectWords.map((word) => {
      return selectWordIndex(word);
    });
    setState((s) => ({ ...s, wordIndexes: wordIndexes }));
  }, [selectWordIndex, selectWords]);

  const updateWordIndex = useCallback(
    (index: number) => {
      const wordIndex = state.wordIndexes[index];
      const selectWord = selectWords[index];
      if (wordIndex && selectWord) {
        const indexes = selectWordIndex(selectWord);
        setState((s) => {
          s.wordIndexes[index] = indexes;
          return { ...s };
        });
      }
    },
    [selectWordIndex, selectWords, state.wordIndexes],
  );

  useEffect(() => {
    updateWordIndexes();
  }, [updateWordIndexes]);

  useEffect(() => {
    const interval = setInterval(() => {
      updateWordIndexes();
    }, 3000);

    return () => {
      clearInterval(interval);
    };
  }, [updateWordIndexes]);

  const onSelectMode = useCallback(
    (_mode: WordSelectionMode, index: number) => {
      useDraftStore.getState().changeSelectWordsMode(index, _mode);
      if (_mode === WordSelectionMode.AIPickWords) {
        updateWordIndex(index);
      }
    },
    [updateWordIndex],
  );

  const onClickMode = useCallback(
    (index: number) => {
      showPopup(
        <SelectWordModePopup
          quickCreateGameMode={state.createType}
          selectedMode={
            selectWordModes[index] ??
            WordSelectionMode.HandpickKeywordsSuggestions
          }
          onPrimary={hidePopup}
          onSelectMode={(_mode) => {
            onSelectMode(_mode, index);
            hidePopup();
          }}
        />,
      );
    },
    [hidePopup, onSelectMode, selectWordModes, showPopup, state.createType],
  );

  const onUnitClick = useCallback((cellIndex: number, unitIndex: number) => {
    useDraftStore.getState().changeSelectWordsUnitType(cellIndex, unitIndex);
  }, []);

  const selectWordCells = useMemo(() => {
    return selectWords.map((word, index) => {
      const wordMode =
        selectWordModes[index] ??
        (mode === QuickCreateGameMode.QuickMemory
          ? WordSelectionMode.AIPickWords
          : WordSelectionMode.HandpickKeywordsSuggestions);
      const indexes = state.wordIndexes[index];
      return {
        mode: wordMode,
        units:
          wordMode === WordSelectionMode.AIPickWords
            ? word.map((unit, unitIndex) => ({
                type: indexes?.includes(unitIndex)
                  ? TextOrderingUnitType.Blank
                  : TextOrderingUnitType.Hint,
                key: unit.key,
              }))
            : word,
      };
    });
  }, [mode, selectWordModes, selectWords, state.wordIndexes]);

  const onClickBack = useCallback(() => {
    navigate(-1);
    setTimeout(() => {
      document.documentElement.style.scrollBehavior = 'auto';
      document.body.style.scrollBehavior = 'auto';
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 10);
  }, [navigate]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
      e.preventDefault();
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const onClickNext = useCallback(() => {
    const showRequiredWarning = selectWords.some((word) =>
      word.every((unit) => unit.type === TextOrderingUnitType.Hint),
    );
    if (showRequiredWarning) {
      setState((s) => ({ ...s, showRequiredWarning: true }));
    } else {
      setState((s) => ({ ...s, showRequiredWarning: false }));
      useDraftStore.getState().convertDraftsToProducts();
      navigate('/create/memory-game/info-settings');
    }
  }, [navigate, selectWords]);

  const onClickWorldSelectGuide = useCallback(() => {
    showPopup(
      <CreateGameWordSelectInfoPopup
        type={
          state.createType === QuickCreateGameMode.QuickMemory
            ? SequenceImageType.QuickMemory
            : state.createType === QuickCreateGameMode.HandWriting
            ? SequenceImageType.Handwriting
            : SequenceImageType.Spelling
        }
        onPrimary={hidePopup}
      />,
    );
  }, [hidePopup, showPopup, state.createType]);

  const expectedPayment = useMemo(() => {
    const gameNum = selectWordCells.length;
    return (
      gameNum * (state.createType === QuickCreateGameMode.QuickMemory ? 3 : 2)
    );
  }, [selectWordCells.length, state.createType]);

  const onClickHelpVideo = useCallback(() => {
    const getVideo =
      userAccount?.settings.webBoosterType === WebBoosterType.Normal
        ? getMemoryBoostCreateVideo
        : getBibleBoostCreateVideo;
    showPopup(
      <VideoPlayer
        url={getVideo(getUiLocale(i18n.language as TranslationLocale))}
        playing={true}
        muted={false}
        // playIcon={<></>}
      />,
      {
        maxWidth: '100%',
        maxHeight: '100%',
        aspectRatio: '16/9',
        withCross: true,
      },
    );
  }, [i18n.language, showPopup, userAccount?.settings.webBoosterType]);

  return (
    <>
      <CreateMemoryGameSelectWordsTemplate
        type={state.createType}
        webBoosterType={userAccount?.settings.webBoosterType}
        locales={drafts.map((draft) => draft.locale)}
        selectWordCells={selectWordCells}
        showRequiredWarning={state.showRequiredWarning}
        onClickHelpVideo={onClickHelpVideo}
        onClickMode={onClickMode}
        onUnitClick={onUnitClick}
        onClickBack={onClickBack}
        onClickNext={onClickNext}
        onClickWorldSelectGuide={onClickWorldSelectGuide}
        expectedPayment={expectedPayment}
      />
    </>
  );
};
export default CreateMemoryGameSelectWordsScreen;
